import React, { useEffect, useRef } from 'react';
import { loadGoogleMaps } from '../lib/loadGoogleMaps';

// A text input enhanced with Google Places Autocomplete.
// Falls back to a plain input if Maps fails to load.
const PlaceInput = ({ value, onChange, placeholder, icon: Icon, testId }) => {
  const inputRef = useRef(null);
  const acRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    loadGoogleMaps()
      .then((google) => {
        if (cancelled || !inputRef.current || !google?.maps?.places) return;
        // Bias results towards Switzerland & the Alps so airports/POIs rank first.
        const bounds = new google.maps.LatLngBounds(
          new google.maps.LatLng(45.75, 5.9),
          new google.maps.LatLng(47.95, 10.55),
        );
        acRef.current = new google.maps.places.Autocomplete(inputRef.current, {
          fields: ['formatted_address', 'name', 'geometry', 'types'],
          componentRestrictions: { country: ['ch', 'de', 'fr', 'it', 'at', 'li'] },
          bounds,
        });
        acRef.current.addListener('place_changed', () => {
          const place = acRef.current.getPlace();
          const name = place.name || '';
          const addr = place.formatted_address || '';
          // For establishments (airports, hotels, stations) keep the POI name in the text,
          // otherwise the field would just show a generic address / country.
          let text;
          if (name && addr && !addr.toLowerCase().startsWith(name.toLowerCase())) {
            text = `${name}, ${addr}`;
          } else {
            text = addr || name || inputRef.current.value;
          }
          onChange(text);
        });
      })
      .catch(() => {
        /* silent fallback to plain input */
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="flex items-center gap-2 border border-gray-200 rounded-xl px-3 py-2.5 focus-within:border-[color:var(--brand-dark)] transition-colors bg-white">
      {Icon && <Icon className="w-4 h-4 text-gray-400 shrink-0" />}
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid={testId}
        className="w-full outline-none text-sm bg-transparent"
      />
    </div>
  );
};

export default PlaceInput;
